// src/components/Profile/Account.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { getSavedJobIds, removeSavedJob } from '../../services/jobService';

const Account = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  
  // Password change states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // Account deletion states
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // General loading state
  const [loading, setLoading] = useState(false);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    
    // Validate inputs
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Please fill in all password fields');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    // Password complexity requirements
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }
    
    if (!/(?=.*[0-9!@#$%^&*])/.test(newPassword)) {
      setPasswordError('Password must contain at least one number or special character');
      return;
    }
    
    try {
      setPasswordLoading(true);
      
      // First verify the current password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });
      
      if (signInError) {
        throw new Error('Current password is incorrect');
      }
      
      // Update the password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });
      
      if (updateError) throw updateError;
      
      setPasswordSuccess('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setPasswordError(error.message || 'Failed to update password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleInitiateDelete = () => {
    setShowDeleteConfirm(true);
    setDeleteError('');
    setDeleteConfirm('');
  };
  
  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') {
      setDeleteError('Please type DELETE to confirm account deletion');
      return;
    }
    
    try {
      setDeleteLoading(true);
      setLoading(true);
      
      // 1. First get all saved job IDs
      const jobIds = await getSavedJobIds(user.id);
      
      // 2. Delete all saved jobs
      if (jobIds && jobIds.length > 0) {
        for (const jobId of jobIds) {
          const success = await removeSavedJob(user.id, jobId);
          if (!success) {
            console.error('Error removing job during account deletion');
            // Continue with other jobs even if one fails
          }
        }
      }
      
      // 3. Delete the user account
      // Note: Supabase client doesn't allow users to delete their own accounts directly
      // This will sign the user out, and you'll need a Supabase Function or backend API
      // to perform the actual account deletion using admin privileges
      const { error: deleteError } = await supabase.auth.signOut();
      
      if (deleteError) throw deleteError;
      
      // Display a message to the user with instructions to contact admin for full account deletion
      alert('Your data has been removed. For complete account deletion, please contact the administrator.');
      
      // 4. Sign out
      await signOut();
      
      // 5. Redirect to home
      navigate('/');
    } catch (error) {
      setDeleteError(error.message || 'Failed to delete account');
      setDeleteLoading(false);
      setLoading(false);
    }
  };

  // Styles
  const containerStyle = {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '20px',
  };

  const sectionStyle = {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '20px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
  };

  const formGroupStyle = {
    marginBottom: '15px',
  };

  const inputStyle = {
    width: '100%',
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '16px',
  };

  const buttonStyle = {
    padding: '10px 15px',
    backgroundColor: '#2980B9',
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '16px',
  };

  const deleteButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#e74c3c',
  };

  const errorStyle = {
    color: '#e74c3c',
    marginTop: '10px',
  };

  const successStyle = {
    color: '#2ecc71',
    marginTop: '10px',
  };
  
  const warningBoxStyle = {
    backgroundColor: '#fff8e1',
    border: '1px solid #ffecb3',
    borderRadius: '4px',
    padding: '15px',
    marginBottom: '20px',
  };

  return (
    <div style={containerStyle}>
      <h1>Account Settings</h1>
      
      {/* Account Information */}
      <div style={sectionStyle}>
        <h2>Account Information</h2>
        <p><strong>Email:</strong> {user?.email}</p>
      </div>
      
      {/* Change Password */}
      <div style={sectionStyle}>
        <h2>Change Password</h2>
        {passwordError && <div style={errorStyle}>{passwordError}</div>}
        {passwordSuccess && <div style={successStyle}>{passwordSuccess}</div>}
        
        <form onSubmit={handlePasswordChange}>
          <div style={formGroupStyle}>
            <label htmlFor="current-password">Current Password</label>
            <input
              type="password"
              id="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              style={inputStyle}
              disabled={passwordLoading}
            />
          </div>
          
          <div style={formGroupStyle}>
            <label htmlFor="new-password">New Password</label>
            <input
              type="password"
              id="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={inputStyle}
              disabled={passwordLoading}
            />
          </div>
          
          <div style={formGroupStyle}>
            <label htmlFor="confirm-password">Confirm New Password</label>
            <input
              type="password"
              id="confirm-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={inputStyle}
              disabled={passwordLoading}
            />
          </div>
          
          <button
            type="submit"
            style={buttonStyle}
            disabled={passwordLoading}
          >
            {passwordLoading ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </div>
      
      {/* Delete Account */}
      <div style={sectionStyle}>
        <h2>Delete Account</h2>
        <p>This action is permanent and cannot be undone. All your saved jobs and account information will be deleted.</p>
        
        {!showDeleteConfirm ? (
          <button
            style={deleteButtonStyle}
            onClick={handleInitiateDelete}
          >
            Delete Account
          </button>
        ) : (
          <div>
            <div style={warningBoxStyle}>
              <h3 style={{ color: '#f39c12', marginTop: 0 }}>Warning: This action cannot be undone</h3>
              <p>Please type DELETE in the field below to confirm:</p>
              
              {deleteError && <div style={errorStyle}>{deleteError}</div>}
              
              <input
                type="text"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                style={inputStyle}
                disabled={deleteLoading}
              />
              
              <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                <button
                  style={deleteButtonStyle}
                  onClick={handleDeleteAccount}
                  disabled={deleteLoading || loading}
                >
                  {deleteLoading ? 'Deleting...' : 'Confirm Delete'}
                </button>
                
                <button
                  style={{
                    ...buttonStyle,
                    backgroundColor: '#7f8c8d',
                  }}
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleteLoading || loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Account;